// Comprehensive ResizeObserver error suppression
export const suppressResizeObserverErrors = () => {
  // Apply in all environments since ResizeObserver errors can happen anywhere
  
  // Store original methods
  const originalConsoleError = console.error;
  const originalConsoleWarn = console.warn;
  const originalWindowError = window.onerror;

  // ResizeObserver error patterns
  const resizeObserverPatterns = [
    'ResizeObserver loop',
    'ResizeObserver loop completed',
    'resize observer loop',
    'non-finite values provided to ResizeObserverEntry',
    'Cannot access before initialization'
  ];

  // Check if message matches ResizeObserver patterns
  const isResizeObserverError = (message: any): boolean => {
    if (!message) return false;
    const messageStr = typeof message === 'string' ? message : String(message);
    return resizeObserverPatterns.some(pattern => 
      messageStr.toLowerCase().includes(pattern.toLowerCase())
    );
  };

  // Override console.error
  console.error = (...args: any[]) => {
    if (args.some(arg => isResizeObserverError(arg))) {
      return; // Suppress the error
    }
    originalConsoleError.apply(console, args);
  };

  // Override console.warn for ResizeObserver warnings
  console.warn = (...args: any[]) => {
    if (args.some(arg => isResizeObserverError(arg))) {
      return; // Suppress the warning
    }
    originalConsoleWarn.apply(console, args);
  };

  // Override window.onerror
  window.onerror = (message, source, lineno, colno, error) => {
    if (isResizeObserverError(message)) {
      return true; // Suppress the error
    }
    if (originalWindowError) {
      return originalWindowError(message, source, lineno, colno, error);
    }
    return false;
  };

  // Override unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    if (
      event.reason &&
      (isResizeObserverError(event.reason.message) || isResizeObserverError(event.reason))
    ) {
      event.preventDefault();
    }
  });

  // Override error events
  window.addEventListener('error', (event) => {
    if (isResizeObserverError(event.message) || isResizeObserverError(event.error?.message)) {
      event.preventDefault();
      event.stopPropagation();
    }
  });

  // Additional ResizeObserver monkey patch to prevent the errors at source
  if (window.ResizeObserver) {
    const OriginalResizeObserver = window.ResizeObserver;
    window.ResizeObserver = class extends OriginalResizeObserver {
      constructor(callback: ResizeObserverCallback) {
        const wrappedCallback = (entries: ResizeObserverEntry[], observer: ResizeObserver) => {
          try {
            callback(entries, observer);
          } catch (error) {
            if (!isResizeObserverError(error)) {
              throw error; // Re-throw non-ResizeObserver errors
            }
            // Suppress ResizeObserver loop errors
          }
        };
        super(wrappedCallback);
      }
    };
  }
};